import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

const SKIP_AUTH = process.env.REACT_APP_SKIP_AUTH === 'true';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: #f5f1eb;
    font-family: 'DM Mono', monospace;
    color: #1c1917;
    min-height: 100vh;
  }

  .lp-root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* ── Header ── */
  .lp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 28px 48px;
    border-bottom: 2px solid #1c1917;
    background: #f5f1eb;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .lp-logo {
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    color: #1c1917;
    text-decoration: none;
    letter-spacing: -0.5px;
    transition: opacity 0.15s;
  }
  .lp-logo:hover { opacity: 0.65; }

  .lp-header-left {
    display: flex;
    align-items: baseline;
    gap: 16px;
  }
  .lp-header-sub {
    font-size: 12px;
    color: #78716c;
    letter-spacing: 0.03em;
  }

  .lp-login-btn {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 20px;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.03em;
    background: #fff;
    color: #1c1917;
    border: 2px solid #1c1917;
    box-shadow: 3px 3px 0 #1c1917;
    cursor: pointer;
    transition: transform 0.1s, box-shadow 0.1s;
  }
  .lp-login-btn:hover {
    transform: translate(-2px, -2px);
    box-shadow: 5px 5px 0 #1c1917;
  }
  .lp-login-btn:active {
    transform: translate(1px, 1px);
    box-shadow: 2px 2px 0 #1c1917;
  }

  .lp-user-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .lp-user-email {
    font-size: 12px;
    color: #78716c;
    letter-spacing: 0.03em;
  }
  .lp-signout-btn {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.05em;
    background: transparent;
    color: #78716c;
    border: 1.5px solid #c4bfb7;
    padding: 6px 14px;
    cursor: pointer;
    transition: color 0.1s, border-color 0.1s;
  }
  .lp-signout-btn:hover { color: #1c1917; border-color: #1c1917; }

  /* ── Section ── */
  .lp-section {
    padding: 40px 48px 80px;
    flex: 1;
  }
  .lp-section-label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #78716c;
    margin-bottom: 24px;
    padding-bottom: 10px;
    border-bottom: 1px solid #d6d0c8;
  }

  /* ── Grid ── */
  .lp-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
    max-width: 1100px;
  }

  /* ── Game Card ── */
  .lp-card {
    background: #fff;
    border: 2px solid #1c1917;
    box-shadow: 4px 4px 0 #1c1917;
    padding: 28px 24px 24px;
    text-decoration: none;
    color: #1c1917;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    cursor: pointer;
  }
  .lp-card:hover {
    transform: translate(-3px, -3px);
    box-shadow: 7px 7px 0 #1c1917;
  }
  .lp-card:active {
    transform: translate(1px, 1px);
    box-shadow: 3px 3px 0 #1c1917;
  }

  .lp-card-icon {
    font-size: 36px;
    line-height: 1;
  }
  .lp-card-name {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    letter-spacing: -0.3px;
  }
  .lp-card-desc {
    font-size: 12px;
    color: #78716c;
    line-height: 1.55;
    letter-spacing: 0.01em;
  }
  .lp-card-tag {
    margin-top: auto;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 4px 8px;
    border: 1.5px solid currentColor;
    align-self: flex-start;
  }
  .lp-card-tag.playable { color: #16a34a; }
  .lp-card-tag.locked   { color: #a8a29e; }

  /* ── Locked card overlay ── */
  .lp-card.lp-locked {
    background: #f5f1eb;
    border-color: #c4bfb7;
    box-shadow: 4px 4px 0 #c4bfb7;
    color: #a8a29e;
    cursor: default;
  }
  .lp-card.lp-locked:hover {
    transform: none;
    box-shadow: 4px 4px 0 #c4bfb7;
  }
  .lp-card.lp-locked .lp-card-icon { filter: grayscale(1); opacity: 0.45; }
  .lp-card.lp-locked .lp-card-name { color: #a8a29e; }

  .lp-lock-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 14px;
    opacity: 0.5;
  }

  /* ── Tooltip ── */
  .lp-tooltip-wrap { position: relative; }
  .lp-tooltip-wrap .lp-tooltip {
    display: none;
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: #1c1917;
    color: #f5f1eb;
    font-size: 11px;
    white-space: nowrap;
    padding: 5px 10px;
    pointer-events: none;
    z-index: 20;
  }
  .lp-tooltip-wrap:hover .lp-tooltip { display: block; }

  @media (max-width: 600px) {
    .lp-header { padding: 20px 24px; }
    .lp-header-left { flex-direction: column; gap: 4px; }
    .lp-section { padding: 0 24px 64px; }
    .lp-grid { grid-template-columns: 1fr 1fr; gap: 14px; }
  }
`;

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface LockedCardProps {
  icon: string;
  name: string;
  desc: string;
}

const LockedCard: React.FC<LockedCardProps> = ({ icon, name, desc }) => (
  <div className="lp-tooltip-wrap">
    <div className="lp-card lp-locked">
      <span className="lp-lock-badge">🔒</span>
      <span className="lp-card-icon">{icon}</span>
      <span className="lp-card-name">{name}</span>
      <span className="lp-card-desc">{desc}</span>
      <span className="lp-card-tag locked">Log in to play</span>
    </div>
    <span className="lp-tooltip">Sign in with Google to unlock</span>
  </div>
);

const LandingPage: React.FC = () => {
  const { user, loading, signIn, signOut } = useAuth();

  return (
    <>
      <style>{styles}</style>
      <div className="lp-root">

        {/* Header */}
        <header className="lp-header">
          <div className="lp-header-left">
            <Link to="/" className="lp-logo">erez.dev</Link>
            <span className="lp-header-sub">A collection of games and experiments. Feel free to look around.</span>
          </div>
          {!loading && (
            user ? (
              <div className="lp-user-info">
                <span className="lp-user-email">{user.email}</span>
                <button className="lp-signout-btn" onClick={signOut}>Sign out</button>
              </div>
            ) : (
              <button className="lp-login-btn" onClick={signIn}>
                <GoogleIcon />
                Log in
              </button>
            )
          )}
        </header>

        {/* Games grid */}
        <main className="lp-section">
          <p className="lp-section-label">Games</p>
          <div className="lp-grid">



            {/* The Protocol — requires auth */}
            {(SKIP_AUTH || user) ? (
              <Link to="/protocol" className="lp-card"
                style={{ borderTopColor: '#7c3aed', boxShadow: '4px 4px 0 #7c3aed' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '7px 7px 0 #7c3aed'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '4px 4px 0 #7c3aed'; }}
                onMouseDown={e => { (e.currentTarget as HTMLElement).style.boxShadow = '3px 3px 0 #7c3aed'; }}
                onMouseUp={e => { (e.currentTarget as HTMLElement).style.boxShadow = '7px 7px 0 #7c3aed'; }}
              >
                <span className="lp-card-icon">⬡</span>
                <span className="lp-card-name">The Protocol</span>
                <span className="lp-card-desc">A real-time multiplayer world. Command your agent. Survive the network.</span>
                <span className="lp-card-tag" style={{ color: '#7c3aed', borderColor: '#7c3aed' }}>Alpha — play now</span>
              </Link>
            ) : (
              <LockedCard
                icon="⬡"
                name="The Protocol"
                desc="A real-time multiplayer world. Command your agent. Survive the network."
              />
            )}

            {/* Locked: Trivia */}
            <LockedCard
              icon="🧠"
              name="Trivia"
              desc="Daily questions. Five categories. One shot."
            />

            {/* Locked: Connections */}
            <LockedCard
              icon="🔗"
              name="Connections"
              desc="Group four things that share a hidden thread."
            />

            {/* Locked: Wordplay */}
            <LockedCard
              icon="🔤"
              name="Wordplay"
              desc="Six letters. Five tries. A new puzzle every day."
            />

          </div>
        </main>

      </div>
    </>
  );
};

export default LandingPage;
